import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, Calendar, Power } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import ImageUploadField from "../../components/admin/ImageUploadField";
import { toast } from "sonner";
import {
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  BTN_OUTLINE,
  BTN_PRIMARY,
  mediaSrc,
} from "../../config/brand";
import { formatDateRange } from "../../lib/slotDates";
import { formatDaysLabelLong } from "../../lib/weekdays";

function createEmptyForm() {
  return {
    name: "",
    description: "",
    theme: "",
    image_url: "",
    center_ids: "",
    duration_10h_price: 240,
    duration_20h_price: 320,
    max_capacity: 10,
  };
}

function stageDaysText(stage) {
  return (
    formatDaysLabelLong(stage.days_of_week || stage.day_of_week) ||
    stage.days_label ||
    "—"
  );
}

function stageDatesText(stage) {
  if (!stage?.start_date) return "Aucun créneau lié";
  return formatDateRange(stage.start_date, stage.end_date) || `${stage.start_date} → ${stage.end_date}`;
}

function clearFieldError(errors, field) {
  if (!errors[field]) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}

export default function AdminStages() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [form, setForm] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadStages = async () => {
    setLoading(true);
    try {
      const response = await authFetch("/api/admin/stages");
      if (response.ok) {
        setStages(await response.json());
      } else {
        toast.error("Impossible de charger les stages");
      }
    } catch {
      toast.error("Impossible de charger les stages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStages();
  }, []);

  const openCreate = () => {
    setEditingStage(null);
    setForm(createEmptyForm());
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (stage) => {
    setEditingStage(stage);
    setForm({
      name: stage.name,
      description: stage.description,
      theme: stage.theme,
      image_url: stage.image_url || "",
      center_ids: (stage.center_ids || []).join(", "),
      duration_10h_price: stage.duration_10h_price,
      duration_20h_price: stage.duration_20h_price,
      max_capacity: stage.max_capacity,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Le nom est requis";
    if (!form.theme.trim()) next.theme = "Le thème est requis";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      theme: form.theme,
      image_url: form.image_url.trim(),
      center_ids: form.center_ids.split(",").map((s) => s.trim()).filter(Boolean),
      duration_10h_price: Number(form.duration_10h_price),
      duration_20h_price: Number(form.duration_20h_price),
      max_capacity: Number(form.max_capacity),
    };

    try {
      const url = editingStage ? `/api/admin/stages/${editingStage.id}` : "/api/admin/stages";
      const method = editingStage ? "PUT" : "POST";

      const response = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur lors de la sauvegarde");
      }

      toast.success(editingStage ? "Stage mis à jour" : "Stage créé");
      setDialogOpen(false);
      loadStages();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (stage) => {
    setTogglingId(stage.id);
    const nextActive = stage.active === false;
    try {
      const res = await authFetch(`/api/admin/stages/${stage.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: nextActive }),
      });
      if (!res.ok) throw new Error("Erreur");
      toast.success(nextActive ? "Stage activé" : "Stage désactivé — les inscriptions existantes sont conservées");
      loadStages();
    } catch {
      toast.error("Impossible de changer le statut");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await authFetch(`/api/admin/stages/${deleteTarget.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Erreur lors de la suppression");
      toast.success(`Stage « ${deleteTarget.name} » supprimé. Les inscriptions existantes sont conservées.`);
      setDeleteTarget(null);
      loadStages();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <AdminPageHeader
        icon={Calendar}
        title="Stages"
        description="Catalogue des thèmes (nom, image, prix). Dates, jours et créneaux se gèrent dans Slots & Dispo."
        action={
          <Button onClick={openCreate} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Nouveau stage
          </Button>
        }
      />

      {loading ? (
        <AdminLoading message="Chargement des stages..." />
      ) : stages.length === 0 ? (
        <AdminEmptyState
          icon={Calendar}
          title="Aucun stage"
          description="Créez un thème de stage, puis ajoutez ses créneaux dans Slots & Dispo."
          action={
            <Button onClick={openCreate} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau stage
            </Button>
          }
        />
      ) : (
        <AdminTable
          mobileCards={stages.map((stage) => (
            <AdminMobileCard
              key={stage.id}
              title={stage.name}
              subtitle={stage.theme}
              onClick={() => openEdit(stage)}
              badge={<Badge variant={stage.active ? "default" : "secondary"}>{stage.active ? "Actif" : "Inactif"}</Badge>}
              meta={[
                { label: "Dates (slots)", value: stageDatesText(stage) },
                { label: "Jours (slots)", value: stageDaysText(stage) },
                {
                  label: "Créneaux",
                  value: stage.open_slots
                    ? `${stage.open_slots} ouvert${stage.open_slots > 1 ? "s" : ""}`
                    : "À configurer",
                },
              ]}
              leading={
                stage.image_url ? (
                  <img
                    src={mediaSrc(stage.image_url)}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover border border-slate-200 shrink-0"
                  />
                ) : null
              }
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(stage)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(stage)}
                    disabled={togglingId === stage.id}
                    className={stage.active === false ? "text-emerald-600" : "text-amber-600"}
                  >
                    {stage.active === false ? "Activer" : "Désactiver"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setDeleteTarget(stage)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={`${adminTableHeadClass} w-14`}>Image</TableHead>
                <TableHead className={adminTableHeadClass}>Nom</TableHead>
                <TableHead className={adminTableHeadClass}>Thème</TableHead>
                <TableHead className={adminTableHeadClass}>Dates (slots)</TableHead>
                <TableHead className={adminTableHeadClass}>Jours (slots)</TableHead>
                <TableHead className={adminTableHeadClass}>Créneaux</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stages.map((stage) => (
                <TableRow key={stage.id} className={adminClickableRowClass} onClick={() => openEdit(stage)}>
                  <TableCell>
                    {stage.image_url ? (
                      <img
                        src={mediaSrc(stage.image_url)}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover border border-slate-200"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50"
                        aria-hidden
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-slate-900 font-medium">{stage.name}</TableCell>
                  <TableCell className={ADMIN_LABEL}>{stage.theme}</TableCell>
                  <TableCell className={ADMIN_LABEL}>
                    {stageDatesText(stage)}
                  </TableCell>
                  <TableCell className={ADMIN_LABEL}>
                    {stageDaysText(stage)}
                  </TableCell>
                  <TableCell className={ADMIN_LABEL}>
                    {stage.open_slots ? (
                      <span className="text-emerald-700">{stage.open_slots} ouvert{stage.open_slots > 1 ? "s" : ""}</span>
                    ) : (
                      <Link to="/admin/slots" className="text-[#3a00e0] hover:underline" onClick={(e) => e.stopPropagation()}>
                        Configurer
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={stage.active ? "default" : "secondary"}>
                      {stage.active ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(stage)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(stage)}
                      disabled={togglingId === stage.id}
                      aria-label={stage.active === false ? "Activer" : "Désactiver"}
                      className={stage.active === false ? "text-emerald-600" : "text-amber-600"}
                    >
                      {togglingId === stage.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeleteTarget(stage)}
                      aria-label={`Supprimer ${stage.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editingStage ? "Modifier le stage" : "Nouveau stage"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="stage-name" className={ADMIN_LABEL}>Nom *</Label>
              <Input
                id="stage-name"
                value={form.name}
                onChange={(e) => {
                  setForm({ ...form, name: e.target.value });
                  if (errors.name) setErrors((prev) => clearFieldError(prev, "name"));
                }}
                className={`${ADMIN_FIELD} ${errors.name ? "border-red-500" : ""}`}
              />
              {errors.name && <p className="text-red-600 text-xs" role="alert">{errors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="stage-description" className={ADMIN_LABEL}>Description</Label>
              <Textarea
                id="stage-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={ADMIN_FIELD}
              />
            </div>
            <ImageUploadField
              label="Image du stage"
              hint="Affichée sur /stages et dans la liste admin. PNG, JPG ou WebP — 5 Mo max. Ou colle une URL."
              kind="stage"
              value={form.image_url}
              onChange={(url) => setForm((prev) => ({ ...prev, image_url: url }))}
              previewAlt="Aperçu image stage"
              previewClassName="h-24 w-36 object-cover rounded-lg border border-slate-200"
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="stage-theme" className={ADMIN_LABEL}>Thème *</Label>
                <Input
                  id="stage-theme"
                  value={form.theme}
                  onChange={(e) => {
                    setForm({ ...form, theme: e.target.value });
                    if (errors.theme) setErrors((prev) => clearFieldError(prev, "theme"));
                  }}
                  placeholder="Arduino, Drone, Lego…"
                  className={`${ADMIN_FIELD} ${errors.theme ? "border-red-500" : ""}`}
                />
                {errors.theme && <p className="text-red-600 text-xs" role="alert">{errors.theme}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="stage-capacity" className={ADMIN_LABEL}>Capacité max</Label>
                <Input
                  id="stage-capacity"
                  type="number"
                  value={form.max_capacity}
                  onChange={(e) => setForm({ ...form, max_capacity: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-sm text-slate-600 space-y-1">
              <p className="font-medium text-slate-800">Dates et créneaux</p>
              <p>
                Les dates, jours et horaires se gèrent uniquement dans{" "}
                <Link to="/admin/slots" className="text-[#3a00e0] hover:underline font-medium">
                  Slots & Dispo
                </Link>
                . Liez chaque créneau à ce stage pour l’afficher sur le site.
              </p>
              {editingStage?.open_slots ? (
                <p className="text-emerald-700">
                  {editingStage.open_slots} créneau{editingStage.open_slots > 1 ? "x" : ""} lié
                  {editingStage.start_date
                    ? ` · ${stageDatesText(editingStage)}${editingStage.days_label ? ` · ${stageDaysText(editingStage)}` : ""}`
                    : ""}
                </p>
              ) : editingStage ? (
                <p className="text-amber-700">Aucun créneau ouvert lié pour l’instant.</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>IDs centres (séparés par virgule)</Label>
              <Input
                value={form.center_ids}
                onChange={(e) => setForm({ ...form, center_ids: e.target.value })}
                placeholder="uuid-centre-1, uuid-centre-2"
                className={ADMIN_FIELD}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Prix 10h (€)</Label>
                <Input
                  type="number"
                  value={form.duration_10h_price}
                  onChange={(e) => setForm({ ...form, duration_10h_price: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Prix 20h (€)</Label>
                <Input
                  type="number"
                  value={form.duration_20h_price}
                  onChange={(e) => setForm({ ...form, duration_20h_price: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title={`Supprimer « ${deleteTarget?.name || ""} » ?`}
        description={`Le stage « ${deleteTarget?.name} » sera définitivement supprimé. Les inscriptions déjà enregistrées sont conservées.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
